import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Card,
  Grid,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  Avatar,
  InputAdornment,
} from "@mui/material";

import {
  Search,
  DirectionsCar,
  LocalShipping,
  CurrencyRupee,
} from "@mui/icons-material";

import {
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";

import { db } from "../../firebase";

function Vehicalmainreport() {
  const [records, setRecords] = useState([]);
  const [vehicleList, setVehicleList] = useState([]);
  const [driverList, setDriverList] = useState([]);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");

  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [toDate, setToDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ================= FETCH DATA =================
  useEffect(() => {
    fetchVehicles();
    fetchDrivers();
    fetchRecords();
  }, []);

  // ================= VEHICLES =================
  const fetchVehicles = async () => {
    try {
      const snap = await getDocs(collection(db, "vehicles"));

      setVehicleList(
        snap.docs.map((doc) => ({
          id: doc.id,
          number: doc.data().vehicleNumber,
        }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  // ================= DRIVERS =================
  const fetchDrivers = async () => {
    try {
      const snap = await getDocs(collection(db, "drivers"));

      setDriverList(
        snap.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  // ================= LOGS =================
  const fetchRecords = async () => {
    setLoading(true);

    try {
      const q = query(
        collection(db, "vehicle_logs"),
        orderBy("createdAt", "desc")
      );

      const snap = await getDocs(q);

      setRecords(
        snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  // ================= FILTER DATA =================
  const filteredData = useMemo(() => {
    return records.filter((r) => {
      const matchesSearch =
        r.person
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        r.description
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        r.vendorName
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        r.location
          ?.toLowerCase()
          .includes(search.toLowerCase());

      const recordDate = r.date || "";

      const matchesDate =
        (!fromDate || recordDate >= fromDate) &&
        (!toDate || recordDate <= toDate);

      return matchesSearch && matchesDate;
    });
  }, [records, search, fromDate, toDate]);

  // ================= TOTALS =================
  const totalExpense = filteredData.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  const totalVehicles = [
    ...new Set(filteredData.map((r) => r.vehicleId)),
  ].length;

  const totalDrivers = [
    ...new Set(filteredData.map((r) => r.driverId)),
  ].length;

  return (
    <Box p={1.5} bgcolor="#f8fafc" minHeight="100vh">
      {/* ================= HEADER ================= */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          background:
            "linear-gradient(135deg,#0f766e,#14b8a6)",
          color: "white",
        }}
      >
        <Typography variant="h5" fontWeight={800}>
          Vehicle Maintenance Report
        </Typography>

        <Typography variant="body2">
          Vehicle logs, maintenance & expense summary
        </Typography>
      </Paper>

      {/* ================= SUMMARY ================= */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={2}>
              <CurrencyRupee color="success" />

              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Total Expense
                </Typography>

                <Typography
                  variant="h5"
                  fontWeight={800}
                  color="success.main"
                >
                  ₹{totalExpense}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={2}>
              <DirectionsCar color="primary" />

              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Total Vehicles
                </Typography>

                <Typography
                  variant="h5"
                  fontWeight={800}
                >
                  {totalVehicles}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={2}>
              <LocalShipping color="warning" />

              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Total Drivers
                </Typography>

                <Typography
                  variant="h5"
                  fontWeight={800}
                >
                  {totalDrivers}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* ================= FILTERS ================= */}
      <Card
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search vendor, person, location..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              fullWidth
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) =>
                setFromDate(e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              fullWidth
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) =>
                setToDate(e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Card>

      {/* ================= TABLE ================= */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
        }}
      >
        <Table>
          <TableHead
            sx={{
              bgcolor: "#f1f5f9",
            }}
          >
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                Sr No
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Image
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Date
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Vehicle
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Driver
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Vendor
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Location
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Description
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Logged By
              </TableCell>

              <TableCell sx={{ fontWeight: 700 }}>
                Amount
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                >
                  <CircularProgress
                    size={28}
                    sx={{ my: 3 }}
                  />
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{ py: 5 }}
                >
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              filteredData
                .slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
                .map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {page * rowsPerPage + index + 1}
                    </TableCell>

                    <TableCell>
                      <Avatar
                        src={row.image}
                        variant="rounded"
                        sx={{
                          width: 45,
                          height: 45,
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      {row.date}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={
                          vehicleList.find(
                            (v) =>
                              v.id === row.vehicleId
                          )?.number || "N/A"
                        }
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      {driverList.find(
                        (d) =>
                          d.id === row.driverId
                      )?.name || "-"}
                    </TableCell>

                    <TableCell>
                      {row.vendorName || "-"}
                    </TableCell>

                    <TableCell>
                      {row.location || "-"}
                    </TableCell>

                    <TableCell
                      sx={{
                        maxWidth: 250,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.description}
                    </TableCell>

                    <TableCell>
                      {row.person}
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: "error.main",
                      }}
                    >
                      ₹{row.amount}
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) =>
            setPage(newPage)
          }
          rowsPerPageOptions={[5, 10, 25, 50]}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(
              parseInt(e.target.value, 10)
            );
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
}

export default Vehicalmainreport;